import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2, CheckSquare, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate } from "@/lib/format";

const STATUS_OPTS = [
  { value: "apen", label: "Åpen" },
  { value: "pagar", label: "Pågår" },
  { value: "ferdig", label: "Ferdig" },
];

function AssigneeSelect({ value, onChange, members, testid }) {
  const known = members.some((m) => m.name === value);
  return (
    <Select value={value ? value : "__none__"} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="mt-1.5"><SelectValue placeholder="Velg ansvarlig" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">Ingen</SelectItem>
        {value && !known ? <SelectItem value={value}>{value}</SelectItem> : null}
        {members.map((m) => (
          <SelectItem key={m.user_id} value={m.name}>{m.name}{m.role ? ` · ${m.role}` : ""}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function Tasks() {
  const { companyId } = useParams();
  const { user } = useAuth();
  const [tasks, setTasks] = useState(null);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assignee_name: "", due_date: "" });
  const [edit, setEdit] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", assignee_name: "", due_date: "" });

  const load = useCallback(() => api.get(`/companies/${companyId}/tasks`).then((r) => setTasks(r.data)), [companyId]);

  useEffect(() => {
    setTasks(null);
    load();
    api.get(`/companies/${companyId}/members`).then((r) => setMembers(r.data)).catch(() => setMembers([]));
  }, [companyId, load]);

  const uname = (user?.name || "").trim().toLowerCase();
  const isMine = (t) => !!uname && (t.assignee_name || "").trim().toLowerCase() === uname;
  const visibleTasks = tasks === null ? null : (showAll ? tasks : tasks.filter(isMine));

  const create = async () => {
    if (!form.title) {
      toast.error("Skriv inn en tittel");
      return;
    }
    try {
      await api.post(`/companies/${companyId}/tasks`, form);
      toast.success("Oppgave opprettet");
      setOpen(false);
      setForm({ title: "", description: "", assignee_name: "", due_date: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openEdit = (t) => {
    setEdit(t);
    setEditForm({ title: t.title || "", description: t.description || "", assignee_name: t.assignee_name || "", due_date: (t.due_date || "").slice(0, 10) });
  };

  const saveEdit = async () => {
    if (!editForm.title.trim()) { toast.error("Skriv inn en tittel"); return; }
    try {
      await api.put(`/tasks/${edit.id}`, editForm);
      toast.success("Oppgave oppdatert");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setStatus = async (t, status) => {
    await api.put(`/tasks/${t.id}`, { status });
    load();
  };

  const remove = async (t) => {
    await api.delete(`/tasks/${t.id}`);
    toast.success("Oppgave slettet");
    load();
  };

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Oppgaver" description="Som standard vises oppgaver du er ansvarlig for. Trykk «Vis alle oppgaver» for å se alle i styret.">
        <Button variant="outline" className="gap-2" data-testid="toggle-all-tasks-btn" onClick={() => setShowAll((s) => !s)}>
          {showAll ? "Vis mine oppgaver" : "Vis alle oppgaver"}
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 active:scale-[0.98]" data-testid="new-task-btn"><Plus className="h-4 w-4" /> Ny oppgave</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ny oppgave</DialogTitle>
              <DialogDescription>Opprett en ny oppgave og tildel en ansvarlig.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Tittel</Label><Input data-testid="task-title-input" className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Beskrivelse</Label><Textarea className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ansvarlig</Label><AssigneeSelect testid="task-assignee-select" value={form.assignee_name} members={members} onChange={(v) => setForm({ ...form, assignee_name: v })} /></div>
                <div><Label>Frist</Label><Input type="date" className="mt-1.5" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Avbryt</Button>
              <Button onClick={create} data-testid="save-task-btn">Opprett</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="space-y-3">
        {visibleTasks === null
          ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)
          : visibleTasks.map((t, i) => (
              <motion.div key={t.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} data-testid={`task-${t.id}`} className={`flex items-start gap-4 rounded-xl border border-border/60 bg-card p-5 shadow-sm ${t.status === "ferdig" ? "opacity-70" : ""}`}>
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><CheckSquare className="h-5 w-5" strokeWidth={1.6} /></div>
                <div className="min-w-0 flex-1">
                  <p className={`font-medium ${t.status === "ferdig" ? "line-through" : ""}`}>{t.title}</p>
                  {t.description ? <p className="mt-0.5 text-sm text-muted-foreground">{t.description}</p> : null}
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {t.assignee_name ? <span>Ansvarlig: {t.assignee_name}</span> : null}
                    {t.due_date ? <span>Frist: {formatDate(t.due_date)}</span> : null}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Select value={t.status} onValueChange={(v) => setStatus(t, v)}>
                    <SelectTrigger className="h-8 w-28 text-xs" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(t)} className="text-muted-foreground hover:text-foreground" data-testid={`edit-task-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(t)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-task-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </motion.div>
            ))}
        {visibleTasks && visibleTasks.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground" data-testid="tasks-empty">
            {showAll
              ? "Ingen oppgaver ennå."
              : (tasks && tasks.length > 0
                  ? "Ingen oppgaver er tildelt deg. Trykk «Vis alle oppgaver» for å se alle oppgaver i styret."
                  : "Ingen oppgaver ennå.")}
          </div>
        ) : null}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rediger oppgave</DialogTitle>
            <DialogDescription>Endre tekst, ansvarlig og frist.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Tittel</Label><Input data-testid="edit-task-title-input" className="mt-1.5" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} /></div>
            <div><Label>Beskrivelse</Label><Textarea data-testid="edit-task-desc-input" className="mt-1.5" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ansvarlig</Label><AssigneeSelect testid="edit-task-assignee-select" value={editForm.assignee_name} members={members} onChange={(v) => setEditForm({ ...editForm, assignee_name: v })} /></div>
              <div><Label>Frist</Label><Input type="date" className="mt-1.5" value={editForm.due_date || ""} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Avbryt</Button>
            <Button onClick={saveEdit} data-testid="update-task-btn">Lagre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
